import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

// Maps role keys to their required addon name
export const ROLE_ADDON_MAP: Record<string, string> = {
  catering: "Catering Dashboard",
  assets: "Assets Dashboard",
  communications: "Communications Dashboard",
  community: "Community Dashboard",
  school_liaison: "School Liaison Dashboard",
  shop: "Shop",
};

export const usePtaAccess = () => {
  const { user, subscription, hasRole, hasPlan } = useAuthContext();
  const queryClient = useQueryClient();

  const isStandardPlan = hasPlan("standard");
  const isSiteAdmin = hasRole("site_admin");

  const { data: activeAddonNames = [], isLoading } = useQuery({
    queryKey: ["ptaActiveAddons", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: memberData } = await supabase
        .from("members")
        .select("pta_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!memberData?.pta_id) return [];

      const { data: subData } = await supabase
        .from("subscriptions")
        .select("id, plan")
        .eq("pta_id", memberData.pta_id)
        .in("status", ["active", "trial"])
        .maybeSingle();

      if (!subData?.id) return [];

      // Standard plan includes everything
      if (subData.plan === "standard") return ["__all__"];

      const { data: addonRecords } = await supabase
        .from("subscription_addons")
        .select("addon_id, pricing_addons(name)")
        .eq("subscription_id", subData.id);

      return (
        addonRecords
          ?.map((r: any) => r.pricing_addons?.name)
          .filter(Boolean) || []
      );
    },
    enabled: !!user?.id,
    staleTime: 0, // Always refetch on mount for access control
    gcTime: 30_000,
  });

  const hasAddon = (addonName: string): boolean => {
    if (isSiteAdmin) return true;
    if (isStandardPlan) return true;
    if (activeAddonNames.includes("__all__")) return true;
    return activeAddonNames.includes(addonName);
  };

  /**
   * Check if a dashboard role is accessible based on plan + addons.
   * Core roles (chair, treasurer, etc.) are always accessible.
   * Add-on roles require Standard plan or purchased addon.
   */
  const canAccessDashboard = (roleKey: string): boolean => {
    if (isSiteAdmin) return true;
    const addonName = ROLE_ADDON_MAP[roleKey];
    if (!addonName) return true; // Core role, always accessible
    return hasAddon(addonName);
  };

  /**
   * Check if a role should be visible in invite/edit forms.
   */
  const isRoleVisible = (roleKey: string, currentlyAssigned?: boolean): boolean => {
    if (currentlyAssigned) return true;
    if (isSiteAdmin || isStandardPlan) return true;
    const addonName = ROLE_ADDON_MAP[roleKey];
    return !addonName || hasAddon(addonName);
  };

  const invalidateAccess = () => {
    queryClient.invalidateQueries({ queryKey: ["ptaActiveAddons"] });
  };

  return {
    activeAddonNames,
    hasAddon,
    canAccessDashboard,
    isRoleVisible,
    isStandardPlan,
    loading: isLoading,
    invalidateAccess,
  };
};
